import { useState } from "react";
import { Grid, Box, IconButton, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useScopeOfWork } from "../../context/ScopeOfWorkContext";
import { useObjectsPagination } from "./hooks/useObjectsPagination";
import { ObjectsCard } from "./ObjectsCard";
import { CleaningObject } from "../../context/scope-of-work.types";
import { ObjectsSearchBar } from "./components/ObjectsSearchBar";
import { ObjectsPlaceholderCard } from "./components/ObjectsPlaceholderCard";
import { colors } from "../../../../../../ui/colors";

const COLORS = {
  orange: { base: colors.orange.base },
  gray: {
    300: "#D1D5DB",
    500: "#6B7280",
    700: "#374151",
  },
};

const ITEMS_PER_PAGE = 12;
const PLACEHOLDER_COUNT = 12;

export const Objects = () => {
  const { state, dispatch } = useScopeOfWork();
  const [search, setSearch] = useState("");

  const { items, isLoading, isFetching, page, totalPages, goNextPage, goPrevPage } = useObjectsPagination(ITEMS_PER_PAGE, search);

  const handleSelectObject = (obj: CleaningObject) => {
    dispatch({ type: "TOGGLE_OBJECT", payload: obj });
  };

  const showLoading = isLoading || isFetching;
  const noResults = !showLoading && items.length === 0;

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      <ObjectsSearchBar value={search} onChange={setSearch} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          py: 1,
        }}
      >
        <IconButton
          onClick={goPrevPage}
          disabled={page === 1}
          sx={{
            color: page === 1 ? COLORS.gray[300] : COLORS.orange.base,
          }}
        >
          <ChevronLeft fontSize="large" />
        </IconButton>

        <Typography fontWeight={700} color={COLORS.gray[700]}>
          Page {page} / {totalPages}
        </Typography>

        <IconButton
          onClick={goNextPage}
          disabled={page === totalPages}
          sx={{
            color: page === totalPages ? COLORS.gray[300] : COLORS.orange.base,
          }}
        >
          <ChevronRight fontSize="large" />
        </IconButton>
      </Box>

      <Grid container spacing={2} mt={1}>
        {showLoading &&
          Array.from({ length: PLACEHOLDER_COUNT })?.map((_, i) => (
            <Grid item xs={6} sm={4} md={3} key={`loading-${i}`}>
              <ObjectsPlaceholderCard />
            </Grid>
          ))}

        {!showLoading &&
          items?.map((object) => (
            <Grid item xs={6} sm={4} md={3} key={object.uid}>
              <ObjectsCard object={object} selected={!!state.objects.find((x) => x.uid === object.uid)} onSelect={() => handleSelectObject(object)} />
            </Grid>
          ))}

        {noResults && (
          <Grid item xs={12}>
            <Box
              sx={{
                width: "100%",
                textAlign: "center",
                py: 6,
                color: COLORS.gray[500],
                fontSize: 16,
                fontWeight: 500,
              }}
            >
              No results found.
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
